import { useState, useEffect, useRef } from"react";
import { useTranslation } from"react-i18next";
import Layout from"../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"../components/ui/card";
import { Button } from"../components/ui/button";
import { Input } from"../components/ui/input";
import { Label } from"../components/ui/label";
import { Badge } from"../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from"../components/ui/dialog";
import { toast } from"sonner";
import axios from"axios";
import { 
 Calculator, 
 Calendar, 
 Users, 
 DollarSign, 
 Save, 
 CheckCircle, 
 Banknote,
 ChevronLeft,
 ChevronRight,
 FileText,
 TrendingUp,
 TrendingDown,
 Edit2,
 Upload,
 AlertCircle,
 RefreshCw,
 Loader2,
 ArrowRight
} from"lucide-react";

export default function PayrollPage({ user, onLogout }) {
 const { t } = useTranslation();
 const [loading, setLoading] = useState(true);
 const [payrollData, setPayrollData] = useState(null);
 const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
 const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
 const [editingRecord, setEditingRecord] = useState(null);
 const [showEditDialog, setShowEditDialog] = useState(false);
 const [saving, setSaving] = useState(false);
 
 // Import state
 const [showImportDialog, setShowImportDialog] = useState(false);
 const [importLoading, setImportLoading] = useState(false);
 const [importPreview, setImportPreview] = useState(null);
 const [importStep, setImportStep] = useState('upload'); // upload, preview, result
 const [importResult, setImportResult] = useState(null);
 const fileInputRef = useRef(null);
 
 const months = [
"January","February","March","April","May","June",
"July","August","September","October","November","December"
 ];
 
 const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

 useEffect(() => {
 loadPayrollData();
 }, [selectedYear, selectedMonth]);

 const loadPayrollData = async () => {
 try {
 setLoading(true);
 const response = await axios.get(`/payroll/month/${selectedYear}/${selectedMonth}`);
 setPayrollData(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 console.error(error);
 } finally {
 setLoading(false);
 }
 };

 const handlePrevMonth = () => {
 if (selectedMonth === 1) {
 setSelectedMonth(12);
 setSelectedYear(selectedYear - 1);
 } else {
 setSelectedMonth(selectedMonth - 1);
 }
 };

 const handleNextMonth = () => {
 if (selectedMonth === 12) {
 setSelectedMonth(1);
 setSelectedYear(selectedYear + 1);
 } else {
 setSelectedMonth(selectedMonth + 1);
 }
 };

 const handleEditRecord = (record) => {
 setEditingRecord({
 ...record,
 days_in_month: 25, // Fixed at 25 working days
 actual_days: record.actual_days || 25,
 days_absent: record.days_absent || 0,
 sick_leave_days: record.sick_leave_days || 0,
 extra_days_worked: record.extra_days_worked || 0,
 notes: record.notes ||""
 });
 setShowEditDialog(true);
 };

 const handleSaveRecord = async () => {
 if (!editingRecord) return;
 
 try {
 setSaving(true);
 await axios.post("/payroll", {
 employee_id: editingRecord.employee_id,
 year: selectedYear,
 month: selectedMonth,
 working_days_in_month: 25, // Fixed at 25
 days_absent: parseInt(editingRecord.days_absent),
 sick_leave_days: parseInt(editingRecord.sick_leave_days) || 0,
 extra_days_worked: parseInt(editingRecord.extra_days_worked),
 notes: editingRecord.notes
 });
 toast.success(t("messages.saveSuccess"));
 setShowEditDialog(false);
 loadPayrollData();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 console.error(error);
 } finally {
 setSaving(false);
 }
 };

 const handleSaveAll = async () => {
 if (!payrollData?.records) return;
 
 try {
 setSaving(true);
 const records = payrollData.records.map(r => ({
 employee_id: r.employee_id,
 working_days_in_month: 25, // Fixed at 25
 days_absent: r.days_absent || 0,
 sick_leave_days: r.sick_leave_days || 0,
 extra_days_worked: r.extra_days_worked || 0,
 notes: r.notes
 }));
 
 await axios.post(`/payroll/batch/${selectedYear}/${selectedMonth}`, records);
 toast.success(t("messages.saveSuccess"));
 loadPayrollData();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 console.error(error);
 } finally {
 setSaving(false);
 }
 };

 const handleApprove = async () => {
 try {
 // First save all
 await handleSaveAll();
 // Then approve
 await axios.post(`/payroll/approve/${selectedYear}/${selectedMonth}`);
 toast.success(t("messages.saveSuccess"));
 loadPayrollData();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 console.error(error);
 }
 };

 const handleMarkPaid = async () => {
 try {
 await axios.post(`/payroll/mark-paid/${selectedYear}/${selectedMonth}`);
 toast.success(t("messages.saveSuccess"));
 loadPayrollData();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 console.error(error);
 }
 };

 // === PAYROLL IMPORT FUNCTIONS ===
 
 const handleImportFileSelect = async (event) => {
 const file = event.target.files[0];
 if (!file) return;
 
 // Validate file type
 const validTypes = ['.csv', '.xlsx', '.xls', '.pdf', '.jpg', '.jpeg', '.png'];
 const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
 if (!validTypes.includes(fileExt)) {
 toast.error(t("validation.required"));
 return;
 }
 
 setImportLoading(true);
 
 try {
 const formData = new FormData();
 formData.append('file', file);
 formData.append('year', selectedYear);
 formData.append('month', selectedMonth);
 
 const response = await axios.post('/payroll/import/preview', formData, {
 headers: { 'Content-Type': 'multipart/form-data' }
 });
 
 setImportPreview(response.data);
 setImportStep('preview');
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to process file");
 } finally {
 setImportLoading(false);
 }
 };
 
 const handleConfirmImport = async () => {
 if (!importPreview) return;
 
 setImportLoading(true);
 
 try {
 const response = await axios.post('/payroll/import/confirm', {
 rows: importPreview.rows,
 year: selectedYear,
 month: selectedMonth
 });
 
 setImportResult(response.data);
 setImportStep('result');
 
 // Reload payroll data
 loadPayrollData();
 
 toast.success(`Import complete: ${response.data.updated} salaries updated`);
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Import failed");
 } finally {
 setImportLoading(false);
 }
 };
 
 const resetImport = () => {
 setShowImportDialog(false);
 setImportStep('upload');
 setImportPreview(null);
 setImportResult(null);
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 };

 const formatCurrency = (amount, currency ="MKD") => {
 if (amount === null || amount === undefined) return"-";
 return new Intl.NumberFormat('en-EU', {
 style: 'currency',
 currency: currency,
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount);
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case"paid":
 return <Badge className="bg-emerald-500/10 text-emerald-700 border-green-300">Paid</Badge>;
 case"approved":
 return <Badge className="bg-blue-500/10 text-blue-700 border-blue-300">Approved</Badge>;
 case"draft":
 return <Badge className="bg-amber-500/10 text-amber-700 border-yellow-200/60">Draft</Badge>;
 default:
 return <Badge variant="outline">Preview</Badge>;
 }
 };

 const getMonthStatus = () => {
 if (!payrollData?.records) return"preview";
 const statuses = payrollData.records.map(r => r.status);
 if (statuses.every(s => s ==="paid")) return"paid";
 if (statuses.every(s => s ==="approved"|| s ==="paid")) return"approved";
 if (statuses.some(s => s ==="draft")) return"draft";
 return"preview";
 };

 const monthStatus = getMonthStatus();

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6"data-testid="payroll-page">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="text-2xl font-bold text-slate-900">{t("payroll.title")}</h1>
 <p className="text-slate-600">Calculate and manage employee salaries</p>
 </div>
 <div className="flex items-center gap-2">
 <Button variant="outline"onClick={() => setShowImportDialog(true)} data-testid="import-payroll-btn">
 <Upload className="w-4 h-4 mr-2"/>
 Import Payroll List
 </Button>
 {monthStatus ==="preview"|| monthStatus ==="draft"? (
 <>
 <Button variant="outline"onClick={handleSaveAll} disabled={saving} data-testid="save-all-btn">
 <Save className="w-4 h-4 mr-2"/>
 Save All
 </Button>
 <Button onClick={handleApprove} disabled={saving} data-testid="approve-btn">
 <CheckCircle className="w-4 h-4 mr-2"/>
 Save & Approve
 </Button>
 </>
 ) : monthStatus ==="approved"? (
 <Button onClick={handleMarkPaid} variant="outline"className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300"data-testid="mark-paid-btn">
 <Banknote className="w-4 h-4 mr-2"/>
 Mark as Paid
 </Button>
 ) : (
 <Badge className="bg-emerald-500/10 text-emerald-700 px-4 py-2 text-sm">
 <CheckCircle className="w-4 h-4 mr-2 inline"/>
 Paid
 </Badge>
 )}
 </div>
 </div>

 {/* Month Selector */}
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-center gap-4">
 <Button variant="outline"size="icon"onClick={handlePrevMonth} data-testid="prev-month-btn">
 <ChevronLeft className="w-4 h-4"/>
 </Button>
 
 <div className="flex items-center gap-2">
 <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
 <SelectTrigger className="w-[150px]"data-testid="month-select">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {months.map((month, index) => (
 <SelectItem key={index + 1} value={(index + 1).toString()}>
 {month}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 
 <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
 <SelectTrigger className="w-[100px]"data-testid="year-select">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {years.map((year) => (
 <SelectItem key={year} value={year.toString()}>
 {year}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 
 <Button variant="outline"size="icon"onClick={handleNextMonth} data-testid="next-month-btn">
 <ChevronRight className="w-4 h-4"/>
 </Button>
 
 <div className="ml-4">
 {getStatusBadge(monthStatus)}
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Summary Cards */}
 {payrollData && (
 <div className="grid grid-cols-4 gap-4">
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Employees</p>
 <p className="text-2xl font-bold text-slate-900">{payrollData.totals?.employee_count || 0}</p>
 </div>
 <Users className="h-7 w-7 text-blue-600"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Total Net Salaries</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(payrollData.totals?.total_net_salary)}</p>
 </div>
 <DollarSign className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Gov. Contributions</p>
 <p className="text-2xl font-bold text-slate-900">
 {formatCurrency(payrollData.records?.reduce((sum, r) => sum + (r.government_contributions || 0), 0))}
 </p>
 </div>
 <FileText className="h-7 w-7 text-amber-600"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Total Cost</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(payrollData.totals?.total_cost)}</p>
 </div>
 <Calculator className="h-7 w-7 text-violet-600"/>
 </div>
 </CardContent>
 </Card>
 </div>
 )}

 {/* Payroll Table */}
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Calendar className="w-5 h-5"/>
 {months[selectedMonth - 1]} {selectedYear} Payroll
 <span className="text-sm font-normal text-slate-500 ml-2">
 ({payrollData?.days_in_month || 0} days)
 </span>
 </CardTitle>
 </CardHeader>
 <CardContent>
 {loading ? (
 <div className="text-center py-8 text-slate-500">Loading...</div>
 ) : !payrollData?.records?.length ? (
 <div className="text-center py-8 text-slate-500">
 <Users className="w-12 h-12 mx-auto mb-2 text-slate-300"/>
 <p>No employees found</p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full"data-testid="payroll-table">
 <thead>
 <tr className="border-b border-slate-200 text-left">
 <th className="pb-3 font-semibold text-slate-600">Employee</th>
 <th className="pb-3 font-semibold text-slate-600 text-center">Days</th>
 <th className="pb-3 font-semibold text-slate-600 text-center">Absent</th>
 <th className="pb-3 font-semibold text-slate-600 text-center">Sick Leave</th>
 <th className="pb-3 font-semibold text-slate-600 text-center">Extra Days</th>
 <th className="pb-3 font-semibold text-slate-600 text-right">Base Salary</th>
 <th className="pb-3 font-semibold text-slate-600 text-right">Net Salary</th>
 <th className="pb-3 font-semibold text-slate-600 text-right">Total Cost</th>
 <th className="pb-3 font-semibold text-slate-600 text-center">Status</th>
 <th className="pb-3 font-semibold text-slate-600 text-center">Actions</th>
 </tr>
 </thead>
 <tbody>
 {payrollData.records.map((record) => {
 const currency = record.salary_currency ||"MKD";
 const adjustment = (record.overtime_addition || 0) - (record.absence_deduction || 0) - (record.sick_leave_deduction || 0);
 // Days is always 25 (fixed working days per month)
 const daysDisplay = 25;
 
 // Calculate pro-rated extra salary
 const proratedExtra = record.actual_days !== record.days_in_month
 ? ((record.extra_salary || 0) / (record.days_in_month || 1)) * (record.actual_days || 0)
 : (record.extra_salary || 0);
 
 // Extra to Pay = Pro-rated Extra + Overtime - Absence - Sick Leave
 const extraToPay = proratedExtra + (record.overtime_addition || 0) - (record.absence_deduction || 0) - (record.sick_leave_deduction || 0);
 
 return (
 <tr key={record.employee_id} className="border-b border-white/40 hover:bg-white/80">
 <td className="py-4">
 <div>
 <p className="font-medium text-slate-900">{record.employee_name}</p>
 <p className="text-[11px] text-slate-500">Daily rate: {formatCurrency(record.daily_rate, currency)}</p>
 </div>
 </td>
 <td className="py-4 text-center">
 <span className="font-medium">
 {daysDisplay}
 </span>
 </td>
 <td className="py-4 text-center">
 {record.days_absent > 0 ? (
 <span className="text-red-600 font-medium flex items-center justify-center gap-1">
 <TrendingDown className="w-3 h-3"/>
 {record.days_absent}
 </span>
 ) : (
 <span className="text-slate-400">0</span>
 )}
 </td>
 <td className="py-4 text-center">
 {(record.sick_leave_days || 0) > 0 ? (
 <span className="text-orange-600 font-medium flex items-center justify-center gap-1">
 <TrendingDown className="w-3 h-3"/>
 {record.sick_leave_days}
 </span>
 ) : (
 <span className="text-slate-400">0</span>
 )}
 </td>
 <td className="py-4 text-center">
 {record.extra_days_worked > 0 ? (
 <span className="text-green-600 font-medium flex items-center justify-center gap-1">
 <TrendingUp className="w-3 h-3"/>
 {record.extra_days_worked}
 </span>
 ) : (
 <span className="text-slate-400">0</span>
 )}
 </td>
 <td className="py-4 text-right font-medium">
 {formatCurrency(record.base_salary, currency)}
 </td>
 <td className="py-4 text-right font-semibold text-slate-900">
 {formatCurrency(record.net_salary, currency)}
 </td>
 <td className="py-4 text-right font-medium text-[#334155]">
 {formatCurrency(record.total_cost, currency)}
 </td>
 <td className="py-4 text-center">
 {getStatusBadge(record.status)}
 </td>
 <td className="py-4 text-center">
 {record.status !=="paid"&& (
 <Button 
 variant="ghost"
 size="sm"
 onClick={() => handleEditRecord(record)}
 data-testid={`edit-${record.employee_id}`}
 >
 <Edit2 className="w-4 h-4"/>
 </Button>
 )}
 </td>
 </tr>
 );
 })}
 </tbody>
 <tfoot>
 <tr className="bg-white/50 font-semibold">
 <td className="py-4 pl-4">Totals</td>
 <td colSpan="5"className="py-4"></td>
 <td className="py-4 text-right text-slate-900">
 {formatCurrency(payrollData.totals?.total_net_salary)}
 </td>
 <td className="py-4 text-right text-[#334155]">
 {formatCurrency(payrollData.totals?.total_cost)}
 </td>
 <td colSpan="2"></td>
 </tr>
 </tfoot>
 </table>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Edit Dialog */}
 <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
 <DialogContent className="max-w-lg">
 <DialogHeader>
 <DialogTitle>Edit Payroll - {editingRecord?.employee_name}</DialogTitle>
 </DialogHeader>
 {editingRecord && (
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Days to Pay</Label>
 <Input 
 type="number"
 value={25}
 disabled
 className="bg-white/60"
 data-testid="edit-days-in-month"
 />
 <p className="text-[11px] text-slate-500 mt-1">
 Fixed at 25 working days
 </p>
 </div>
 <div>
 <Label>Days Absent</Label>
 <Input 
 type="number"
 value={editingRecord.days_absent}
 onChange={(e) => setEditingRecord({...editingRecord, days_absent: e.target.value})}
 data-testid="edit-days-absent"
 />
 <p className="text-xs text-red-500 mt-1">
 Deducts from total salary
 </p>
 </div>
 </div>
 
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Sick Leave Days</Label>
 <Input 
 type="number"
 value={editingRecord.sick_leave_days}
 onChange={(e) => setEditingRecord({...editingRecord, sick_leave_days: e.target.value})}
 data-testid="edit-sick-leave-days"
 />
 <p className="text-xs text-orange-500 mt-1">
 Deducts from base salary only
 </p>
 </div>
 <div>
 <Label>Extra Days Worked</Label>
 <Input 
 type="number"
 value={editingRecord.extra_days_worked}
 onChange={(e) => setEditingRecord({...editingRecord, extra_days_worked: e.target.value})}
 data-testid="edit-extra-days"
 />
 <p className="text-xs text-green-500 mt-1">
 Adds to total salary
 </p>
 </div>
 </div>
 
 <div>
 <Label>Notes</Label>
 <Input 
 value={editingRecord.notes}
 onChange={(e) => setEditingRecord({...editingRecord, notes: e.target.value})}
 placeholder="Optional notes about this month's payroll"
 data-testid="edit-notes"
 />
 </div>

 {/* Calculation Preview */}
 <div className="p-4 bg-white/50 rounded-sm space-y-3">
 <h4 className="font-semibold text-[#334155]">Calculation Preview</h4>
 
 {/* Fixed amounts */}
 <div className="grid grid-cols-2 gap-2 text-sm border-b border-slate-200 pb-3">
 <span className="text-slate-500">Full Month Salary (Base + Extra):</span>
 <span className="text-right font-medium">{formatCurrency((editingRecord.base_salary || 0) + (editingRecord.extra_salary || 0), editingRecord.salary_currency)}</span>
 
 <span className="text-slate-500">Daily Rate (Total / 25):</span>
 <span className="text-right text-slate-600">
 {formatCurrency(((editingRecord.base_salary || 0) + (editingRecord.extra_salary || 0)) / 25, editingRecord.salary_currency)} / day
 </span>
 
 <span className="text-slate-500">Base Daily Rate (Base / 25):</span>
 <span className="text-right text-slate-600">
 {formatCurrency((editingRecord.base_salary || 0) / 25, editingRecord.salary_currency)} / day
 </span>
 </div>
 
 {/* Adjustments */}
 <div className="grid grid-cols-2 gap-2 text-sm border-b border-slate-200 pb-3">
 <span className="text-red-600">Absence ({editingRecord.days_absent || 0} days):</span>
 <span className="text-right text-red-600 font-medium">
 -{formatCurrency((((editingRecord.base_salary || 0) + (editingRecord.extra_salary || 0)) / 25) * (parseInt(editingRecord.days_absent) || 0), editingRecord.salary_currency)}
 </span>
 
 <span className="text-orange-600">Sick Leave ({editingRecord.sick_leave_days || 0} days):</span>
 <span className="text-right text-orange-600 font-medium">
 -{formatCurrency(((editingRecord.base_salary || 0) / 25) * (parseInt(editingRecord.sick_leave_days) || 0), editingRecord.salary_currency)}
 </span>
 
 <span className="text-green-600">Overtime ({editingRecord.extra_days_worked || 0} days):</span>
 <span className="text-right text-green-600 font-medium">
 +{formatCurrency((((editingRecord.base_salary || 0) + (editingRecord.extra_salary || 0)) / 25) * (parseInt(editingRecord.extra_days_worked) || 0), editingRecord.salary_currency)}
 </span>
 </div>
 
 {/* Net Salary Summary */}
 <div className="p-4 bg-white/50 rounded-sm border border-slate-200">
 <div className="flex justify-between items-center">
 <div>
 <p className="text-sm text-slate-500 font-medium">Net Salary This Month</p>
 <p className="text-xs text-gray-400">Base + Extra - Absence - Sick Leave + Overtime</p>
 </div>
 <div className="text-right">
 <p className="text-2xl font-bold text-slate-900">
 {formatCurrency(
 ((editingRecord.base_salary || 0) + (editingRecord.extra_salary || 0)) - 
 ((((editingRecord.base_salary || 0) + (editingRecord.extra_salary || 0)) / 25) * (parseInt(editingRecord.days_absent) || 0)) -
 (((editingRecord.base_salary || 0) / 25) * (parseInt(editingRecord.sick_leave_days) || 0)) +
 ((((editingRecord.base_salary || 0) + (editingRecord.extra_salary || 0)) / 25) * (parseInt(editingRecord.extra_days_worked) || 0)),
 editingRecord.salary_currency
 )}
 </p>
 </div>
 </div>
 </div>
 </div>
 </div>
 )}
 <DialogFooter>
 <Button variant="outline"onClick={() => setShowEditDialog(false)}>Cancel</Button>
 <Button onClick={handleSaveRecord} disabled={saving} data-testid="save-record-btn">
 {saving ?"Saving...":"Save"}
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Import Payroll Dialog */}
 <Dialog open={showImportDialog} onOpenChange={(open) => { if (!open) resetImport(); }}>
 <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <Upload className="w-5 h-5"/>
 Import Payroll List - {months[selectedMonth - 1]} {selectedYear}
 </DialogTitle>
 </DialogHeader>
 
 {/* Step 1: Upload */}
 {importStep === 'upload' && (
 <div className="py-8">
 <div className="border-2 border-dashed border-slate-200 rounded-sm p-8 text-center hover:border-blue-400 transition-colors">
 <input
 ref={fileInputRef}
 type="file"
 accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png"
 onChange={handleImportFileSelect}
 className="hidden"
 id="payroll-import-file"
 />
 {importLoading ? (
 <div className="flex flex-col items-center">
 <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4"/>
 <p className="text-slate-600">Processing file...</p>
 <p className="text-sm text-slate-500">Extracting and matching employees</p>
 </div>
 ) : (
 <>
 <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4"/>
 <p className="text-lg font-medium text-slate-900 mb-2">
 Upload Payroll Document for {months[selectedMonth - 1]}
 </p>
 <p className="text-slate-500 mb-4">
 Supports: CSV, Excel, PDF, Images
 </p>
 <Button onClick={() => fileInputRef.current?.click()}>
 <Upload className="w-4 h-4 mr-2"/>
 Select File
 </Button>
 <div className="mt-6 text-left bg-blue-50/80 p-4 rounded-sm">
 <h4 className="font-semibold text-blue-900 mb-2">What this does:</h4>
 <ul className="text-sm text-blue-800 space-y-1">
 <li>• Matches employees by name or Mat.Br (ID)</li>
 <li>• Compares new Base Salary with current salary</li>
 <li>• Updates ONLY the Base Salary if changed</li>
 <li>• <strong>Never touches Bonus/Extra salaries</strong></li>
 </ul>
 </div>
 </>
 )}
 </div>
 </div>
 )}
 
 {/* Step 2: Preview */}
 {importStep === 'preview' && importPreview && (
 <div className="py-4">
 {/* Summary */}
 <div className="grid grid-cols-3 gap-4 mb-6">
 <Card className="">
 <CardContent className="pt-4 pb-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs text-blue-600">Salary Changes</p>
 <p className="text-2xl font-bold text-slate-900">{importPreview.changes_count || 0}</p>
 </div>
 <RefreshCw className="w-6 h-6 text-blue-600"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-4 pb-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs text-slate-600">No Change</p>
 <p className="text-2xl font-bold text-slate-900">{importPreview.no_change_count || 0}</p>
 </div>
 <ArrowRight className="w-6 h-6 text-gray-400"/>
 </div>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="pt-4 pb-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs text-red-600">Not Found</p>
 <p className="text-2xl font-bold text-slate-900">{importPreview.not_found_count || 0}</p>
 </div>
 <AlertCircle className="w-6 h-6 text-red-500"/>
 </div>
 </CardContent>
 </Card>
 </div>
 
 {/* Preview Table */}
 <div className="border rounded-sm overflow-hidden max-h-80 overflow-y-auto">
 <table className="w-full text-sm">
 <thead className="bg-white/60 sticky top-0">
 <tr>
 <th className="px-3 py-2 text-left font-medium text-[#334155]">Employee</th>
 <th className="px-3 py-2 text-right font-medium text-[#334155]">Current Base</th>
 <th className="px-3 py-2 text-center font-medium text-[#334155]"></th>
 <th className="px-3 py-2 text-right font-medium text-[#334155]">New Base</th>
 <th className="px-3 py-2 text-right font-medium text-[#334155]">Change</th>
 <th className="px-3 py-2 text-center font-medium text-[#334155]">Status</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-200">
 {importPreview.rows?.map((row, idx) => (
 <tr key={idx} className={row.status === 'NOT_FOUND' ? 'bg-red-50/80' : row.status === 'CHANGED' ? 'bg-blue-50/80' : ''}>
 <td className="px-3 py-2">
 <span className="font-medium text-slate-900">{row.employee_name}</span>
 {row.mat_br && <span className="text-[11px] text-slate-500 block">ID: {row.mat_br}</span>}
 </td>
 <td className="px-3 py-2 text-right font-mono">
 {row.current_salary ? `${row.current_salary.toLocaleString()} MKD` : '-'}
 </td>
 <td className="px-3 py-2 text-center">
 {row.status === 'CHANGED' && <ArrowRight className="w-4 h-4 text-blue-500 mx-auto"/>}
 </td>
 <td className="px-3 py-2 text-right font-mono font-medium">
 {row.new_salary?.toLocaleString()} MKD
 </td>
 <td className="px-3 py-2 text-right">
 {row.status === 'CHANGED' && (
 <span className={`font-medium ${row.salary_diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
 {row.salary_diff > 0 ? '+' : ''}{row.salary_diff?.toLocaleString()} MKD
 </span>
 )}
 </td>
 <td className="px-3 py-2 text-center">
 {row.status === 'CHANGED' && (
 <Badge className="bg-blue-500/10 text-blue-700">
 <TrendingUp className="w-3 h-3 mr-1"/>Update
 </Badge>
 )}
 {row.status === 'NO_CHANGE' && (
 <Badge className="bg-gray-500/10 text-slate-600">Same</Badge>
 )}
 {row.status === 'NOT_FOUND' && (
 <Badge className="bg-rose-500/10 text-rose-700">Not Found</Badge>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 
 <DialogFooter className="mt-6">
 <Button variant="outline"onClick={resetImport}>Cancel</Button>
 <Button 
 onClick={handleConfirmImport} 
 disabled={importLoading || (importPreview.changes_count || 0) === 0}
 >
 {importLoading ? (
 <>
 <Loader2 className="w-4 h-4 mr-2 animate-spin"/>
 Updating...
 </>
 ) : (
 <>
 <CheckCircle className="w-4 h-4 mr-2"/>
 Update {importPreview.changes_count || 0} Salaries
 </>
 )}
 </Button>
 </DialogFooter>
 </div>
 )}
 
 {/* Step 3: Result */}
 {importStep === 'result' && importResult && (
 <div className="py-8 text-center">
 <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4"/>
 <h3 className="text-xl font-semibold text-slate-900 mb-2">Salaries Updated!</h3>
 <p className="text-slate-600 mb-6">
 {importResult.updated} employee base salaries have been updated for {months[selectedMonth - 1]} {selectedYear}
 </p>
 <div className="bg-white/50 rounded-sm p-4 max-w-sm mx-auto mb-6">
 <div className="flex justify-between text-sm mb-2">
 <span className="text-slate-600">Updated:</span>
 <span className="font-medium text-blue-700">{importResult.updated}</span>
 </div>
 <div className="flex justify-between text-sm mb-2">
 <span className="text-slate-600">No Change:</span>
 <span className="font-medium text-slate-600">{importResult.no_change}</span>
 </div>
 <div className="flex justify-between text-sm">
 <span className="text-slate-600">Not Found:</span>
 <span className="font-medium text-red-600">{importResult.not_found}</span>
 </div>
 </div>
 <Button onClick={resetImport}>Done</Button>
 </div>
 )}
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
}
